import { useCallback, useState, useEffect, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, X, AlertCircle, CheckCircle, Brain } from 'lucide-react'
import { IMAGE_CONSTRAINTS } from '../lib/constants'
import { initializeEnhancedAnalysis, analyzeImageEnhanced } from '../services/enhancedImageAnalysis'
import toast from 'react-hot-toast'

const SUPPORTED_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif',
  'image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence',
  'image/avif', 'image/tiff', 'image/tif',
  '' // Chrome on Mac reports empty MIME for HEIC
]

const BROWSER_DECODABLE = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

// Detect HEIC by magic bytes (most reliable — MIME type is often wrong/empty on Mac/Chrome)
async function isHeicFile(file: File): Promise<boolean> {
  const heicMimes = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']
  if (heicMimes.includes(file.type.toLowerCase())) return true
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (['heic', 'heif', 'heics'].includes(ext || '')) return true
  // Magic byte check: ISOBMFF container — bytes 4-7 = "ftyp", bytes 8-11 = brand
  try {
    const buf = await file.slice(0, 12).arrayBuffer()
    const view = new Uint8Array(buf)
    const ftyp = String.fromCharCode(view[4], view[5], view[6], view[7])
    if (ftyp !== 'ftyp') return false
    const brand = String.fromCharCode(view[8], view[9], view[10], view[11])
    return ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1'].includes(brand)
  } catch {
    return false
  }
}

function isBrowserDecodable(file: File): boolean {
  return BROWSER_DECODABLE.includes(file.type.toLowerCase()) ||
    file.name.toLowerCase().match(/\.(jpe?g|png|webp|gif)$/i) !== null
}

// Convert HEIC to JPEG blob.
// Strategy 1: heic-to (WASM, client-side, no server needed, uses libheif 1.21.2)
// Strategy 2: server-side Sharp conversion as fallback
async function convertHeicToJpeg(file: File): Promise<File> {
  const outName = file.name.replace(/\.(heic|heif|heics)$/i, '.jpg')

  // --- Strategy 1: heic-to (WASM, runs entirely in browser) ---
  try {
    const { heicTo } = await import('heic-to')
    const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.85 })
    console.log('✅ HEIC converted via heic-to (WASM)')
    return new File([jpegBlob], outName, { type: 'image/jpeg' })
  } catch (wasmErr) {
    console.warn('⚠️ heic-to WASM failed, trying server fallback:', wasmErr)
  }

  // --- Strategy 2: server-side Sharp ---
  try {
    const formData = new FormData()
    formData.append('image', file)
    const res = await fetch('/api/upload/convert-heic', { method: 'POST', body: formData })
    if (!res.ok) throw new Error(`Server ${res.status}`)
    const jpegBlob = await res.blob()
    console.log('✅ HEIC converted via server Sharp')
    return new File([jpegBlob], outName, { type: 'image/jpeg' })
  } catch (serverErr) {
    console.warn('⚠️ Server HEIC conversion failed:', serverErr)
  }

  throw new Error('HEIC conversion failed on both client and server')
}

interface FileUploadProps {
  onUploadSuccess: (fileData: any) => void
  onUploadError?: (error: string) => void
  disabled?: boolean
  initialFile?: any // restore preview after tab switch without re-uploading
}

export function FileUpload({ onUploadSuccess, onUploadError, disabled = false, initialFile }: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<any>(initialFile || null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [modelStatus, setModelStatus] = useState<'loading' | 'ready' | 'error' | 'not-initialized'>('not-initialized')
  const modelStatusRef = useRef(modelStatus)

  // Keep ref in sync with state
  useEffect(() => {
    modelStatusRef.current = modelStatus
  }, [modelStatus])

  // Initialize COCO-SSD model on component mount
  useEffect(() => {
    const initModel = async () => {
      try {
        setModelStatus('loading')
        await initializeEnhancedAnalysis()
        setModelStatus('ready')
        console.log('✅ Enhanced analysis models ready')
      } catch (error) {
        console.error('❌ Failed to initialize models:', error)
        // Don't block the user — let them upload and add keywords manually
        setModelStatus('error')
        // Don't show error toast — show a softer message in the UI instead
      }
    }
    
    initModel()
  }, [])

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      if (acceptedFiles.length === 0) return

      const file = acceptedFiles[0]
      console.log('📁 File dropped:', { name: file.name, type: file.type, size: file.size })

      // Validate file size
      if (file.size > IMAGE_CONSTRAINTS.MAX_FILE_SIZE_MB * 1024 * 1024) {
        const error = `File too large. Maximum size is ${IMAGE_CONSTRAINTS.MAX_FILE_SIZE_MB}MB.`
        toast.error(error)
        onUploadError?.(error)
        return
      }

      // Validate file type
      const isHeic = await isHeicFile(file)
      const isSupported = SUPPORTED_TYPES.includes(file.type.toLowerCase()) ||
        file.type.startsWith('image/') ||
        file.name.toLowerCase().match(/\.(jpe?g|png|webp|gif|heic|heif|avif|tiff?)$/i)

      if (!isSupported && !isHeic) {
        const error = 'Unsupported format. Please upload JPEG, PNG, WebP, HEIC, AVIF, or TIFF images.'
        toast.error(error)
        onUploadError?.(error)
        return
      }

      setIsUploading(true)
      setIsAnalyzing(true)

      // Convert HEIC to JPEG for AI analysis
      let analysisFile = file
      if (isHeic) {
        try {
          console.log('🔄 Converting HEIC to JPEG...')
          toast.loading('Converting HEIC...', { id: 'heic-convert' })
          analysisFile = await convertHeicToJpeg(file)
          toast.dismiss('heic-convert')
        } catch (convErr) {
          toast.dismiss('heic-convert')
          console.warn('⚠️ HEIC conversion failed, skipping AI analysis:', convErr)
          toast('HEIC accepted — add keywords to generate captions.', { icon: '💡' })
          analysisFile = file
        }
      }

      // Check if we can run AI analysis
      const canAnalyze = isBrowserDecodable(analysisFile) && modelStatusRef.current === 'ready'
      
      let analysisResult
      
      if (canAnalyze) {
        // Run AI analysis
        console.log('🔍 Running AI analysis...')
        toast.loading('Analyzing image with AI...', { id: 'analysis' })
        
        try {
          analysisResult = await analyzeImageEnhanced(analysisFile)
          console.log('✅ AI analysis complete:', analysisResult)
          toast.dismiss('analysis')
          
          if (analysisResult.displayKeywords.length > 0) {
            toast.success(`Detected: ${analysisResult.displayKeywords.join(', ')}`)
          } else {
            toast('Add keywords below to generate captions.', { icon: '💡' })
          }
        } catch (analysisError) {
          console.error('❌ AI analysis failed:', analysisError)
          toast.dismiss('analysis')
          toast('Add keywords below to generate captions.', { icon: '💡' })
          
          analysisResult = {
            allDetectedKeywords: [], keywords: [], displayKeywords: [],
            displayKeywordsWithConfidence: [], lowConfidenceKeywords: [],
            objectDetails: [], objects: [], scene: 'lifestyle', sceneConfidence: 0,
            detectedText: [], hasText: false, dominantColors: [], colorMood: 'neutral',
            gender: null, genderConfidence: 0, confidence: 0, mood: 'neutral', category: 'lifestyle'
          }
        }
      } else {
        console.log('⚠️ AI models not ready or unsupported format')
        toast('Add keywords below to generate captions.', { icon: '💡' })
        
        analysisResult = {
          allDetectedKeywords: [], keywords: [], displayKeywords: [],
          displayKeywordsWithConfidence: [], lowConfidenceKeywords: [],
          objectDetails: [], objects: [], scene: 'lifestyle', sceneConfidence: 0,
          detectedText: [], hasText: false, dominantColors: [], colorMood: 'neutral',
          gender: null, genderConfidence: 0, confidence: 0, mood: 'neutral', category: 'lifestyle'
        }
      }

      setIsAnalyzing(false)

      // Convert to COCO-SSD format for backward compatibility
      const cocoSsdAnalysis = {
        keywords: analysisResult.allDetectedKeywords,
        highConfKeywords: analysisResult.keywords,
        lowConfidenceKeywords: analysisResult.lowConfidenceKeywords,
        detections: analysisResult.objectDetails,
        confidence: analysisResult.confidence,
        gender: analysisResult.gender,
        genderConfidence: analysisResult.genderConfidence
      }

      // Create local file reference — use converted JPEG for preview if HEIC
      const previewFile = isHeic && analysisFile !== file ? analysisFile : file
      const localFileUrl = URL.createObjectURL(previewFile)
      
      const result = {
        filename: `local-${Date.now()}-${file.name}`,
        originalName: file.name,
        size: file.size,
        mimeType: file.type,
        url: localFileUrl,
        localFile: previewFile, // use JPEG for preview rendering
        cocoSsdAnalysis: cocoSsdAnalysis,
        enhancedAnalysis: analysisResult,
        aiKeywords: analysisResult.displayKeywords || [],
        aiKeywordsWithConfidence: analysisResult.displayKeywordsWithConfidence || [],
        allKeywords: analysisResult.allDetectedKeywords,
        lowConfidenceKeywords: analysisResult.lowConfidenceKeywords,
        gender: analysisResult.gender,
        visionContext: {
          cocoSsdDetections: analysisResult.objectDetails,
          aiKeywords: analysisResult.keywords,
          allKeywords: analysisResult.allDetectedKeywords,
          lowConfidenceKeywords: analysisResult.lowConfidenceKeywords,
          displayKeywords: analysisResult.displayKeywords,
          displayKeywordsWithConfidence: analysisResult.displayKeywordsWithConfidence,
          gender: analysisResult.gender,
          confidence: analysisResult.confidence,
          source: 'browser-only-analysis'
        },
        metadata: { width: 0, height: 0 }
      }
      
      // Get image dimensions if possible
      if (isBrowserDecodable(previewFile)) {
        const img = new Image()
        img.onload = () => {
          result.metadata.width = img.width
          result.metadata.height = img.height
        }
        img.src = localFileUrl
      }
      
      setUploadedFile(result)
      onUploadSuccess(result)
      
    } catch (fatalError) {
      console.error('Fatal error in onDrop:', fatalError)
      toast.error('Something went wrong. Please try a different image.')
      setIsUploading(false)
      setIsAnalyzing(false)
    }
  }, [onUploadSuccess, onUploadError, modelStatus])

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    disabled: disabled || isUploading,
    onDropRejected: (rejectedFiles) => {
      console.log('Files rejected by dropzone:', rejectedFiles)
      const file = rejectedFiles[0]
      if (file?.errors?.some(e => e.code === 'too-many-files')) {
        toast.error('Please upload one file at a time.')
      }
    }
  })

  const clearUpload = () => {
    setUploadedFile(null)
    setIsUploading(false)
    setIsAnalyzing(false)
  }

  // Determine the current state for styling and messages
  const hasRejectedFiles = isDragReject || fileRejections.length > 0
  const isActiveAndValid = isDragActive && !hasRejectedFiles

  // Show uploaded file preview
  if (uploadedFile) {
    return (
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(142,143,250,0.2)',
        borderRadius: '16px',
        padding: '1.25rem'
      }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-6 w-6" style={{ color: '#e040fb' }} />
            <div>
              <p className="font-medium" style={{ color: '#ffffff' }}>Upload Complete</p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)', wordBreak: 'break-all', maxWidth: '160px' }}>{uploadedFile.originalName}</p>
            </div>
          </div>
          <button
            onClick={clearUpload}
            className="transition-colors"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#e040fb')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {uploadedFile.url && isBrowserDecodable(uploadedFile.localFile) && (
          <div className="mt-4" style={{
            border: '1px solid rgba(142,143,250,0.3)',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 0 16px rgba(142,143,250,0.1)'
          }}>
            <img
              src={uploadedFile.url}
              alt={uploadedFile.originalName}
              className="max-w-full h-48 object-cover w-full"
            />
          </div>
        )}

        <div className="mt-4 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <p>Size: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
          {uploadedFile.metadata && uploadedFile.metadata.width > 0 && (
            <p>Dimensions: {uploadedFile.metadata.width} × {uploadedFile.metadata.height}</p>
          )}
          <div className="mt-2 p-2" style={{
            background: 'rgba(142,143,250,0.06)',
            border: '1px solid rgba(142,143,250,0.2)',
            borderRadius: '8px',
            boxShadow: '0 0 10px rgba(142,143,250,0.1)'
          }}>
            <p className="text-xs font-medium" style={{ color: '#D1D1F7' }}>✅ Ready to generate captions!</p>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>🔒 Privacy mode: Image stays on your device</p>
          </div>
        </div>
      </div>
    )
  }

  // Show upload progress or analysis
  if (isUploading && isAnalyzing) {
    return (
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(142,143,250,0.2)',
        borderRadius: '16px',
        padding: '1.25rem'
      }}>
        <div className="flex justify-center mb-4">
          <Brain className="h-8 w-8 animate-pulse" style={{ color: '#e040fb' }} />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium" style={{ color: '#D1D1F7' }}>Processing Image...</p>
          <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Analyzing with AI</p>
        </div>
      </div>
    )
  }

  // Show AI model loading state — show upload area with subtle loading indicator
  // Don't block mobile users who may be on slow connections
  if (modelStatus === 'loading' || modelStatus === 'not-initialized') {
    return (
      <div
        {...getRootProps()}
        style={{
          border: '2px dashed rgba(142,143,250,0.25)',
          borderRadius: '16px',
          padding: '48px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          background: 'rgba(255,255,255,0.02)',
        }}
      >
        <input {...getInputProps()} accept="image/heic,image/heif,.heic,.heif,image/jpeg,image/png,image/webp,image/avif,image/tiff,image/*" />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <div style={{ position: 'relative', width: '56px', height: '56px' }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              border: '2px solid transparent',
              borderTopColor: '#8E8FFA',
              borderRightColor: '#e040fb',
              animation: 'spin 1.2s linear infinite'
            }} />
            <div style={{
              position: 'absolute', inset: '10px', borderRadius: '50%',
              background: 'rgba(142,143,250,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Brain style={{ width: '18px', height: '18px', color: '#8E8FFA' }} />
            </div>
          </div>
          <div>
            <p style={{ color: '#ffffff', fontSize: '1rem', fontWeight: 600, marginBottom: '6px' }}>
              <span className="hidden sm:inline">Drag & drop a file, or click to select</span>
              <span className="sm:hidden">Tap to upload your photo</span>
            </p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>Loading AI model…</p>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', marginTop: '10px' }}>
              JPEG, PNG, WebP, HEIC, AVIF, TIFF up to {IMAGE_CONSTRAINTS.MAX_FILE_SIZE_MB}MB
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Show upload area
  return (
    <div
      {...getRootProps()}
      style={{
        border: `2px dashed ${hasRejectedFiles ? '#f87171' : isActiveAndValid ? '#a78bfa' : 'rgba(255,255,255,0.15)'}`,
        borderRadius: '16px',
        padding: '48px 24px',
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        background: isActiveAndValid ? 'rgba(142,143,250,0.08)' : hasRejectedFiles ? 'rgba(248,113,113,0.05)' : 'rgba(255,255,255,0.02)',
        opacity: disabled ? 0.5 : 1
      }}
    >
      <input {...getInputProps()} accept="image/heic,image/heif,.heic,.heif,image/jpeg,image/png,image/webp,image/avif,image/tiff,image/*" />
      
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: hasRejectedFiles ? 'rgba(248,113,113,0.15)' : 'rgba(142,143,250,0.1)',
          border: `1px solid ${hasRejectedFiles ? 'rgba(248,113,113,0.3)' : 'rgba(142,143,250,0.2)'}`
        }}>
          {hasRejectedFiles ? (
            <AlertCircle className="w-7 h-7" style={{ color: '#f87171' }} />
          ) : (
            <Upload className="w-7 h-7" style={{ color: '#8E8FFA' }} />
          )}
        </div>
        
        <div>
          {hasRejectedFiles ? (
            <p style={{ color: '#f87171', fontSize: '1rem', fontWeight: 600 }}>Invalid file type or size</p>
          ) : isActiveAndValid ? (
            <p style={{ color: '#D1D1F7', fontSize: '1rem', fontWeight: 600 }}>Drop your file here</p>
          ) : (
            <>
              <p style={{
                background: 'linear-gradient(90deg, #ffffff 0%, #D1D1F7 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: '1rem',
                fontWeight: 600,
                marginBottom: '6px'
              }}>
                <span className="hidden sm:inline">Drag & drop a file, or click to select</span>
                <span className="sm:hidden">Tap to upload your photo</span>
              </p>
              {modelStatus === 'ready' ? (
                <p style={{ color: '#8E8FFA', fontSize: '0.82rem' }}>🔍 AI ready to analyze your image!</p>
              ) : (
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>⚠️ AI analysis unavailable — add keywords manually</p>
              )}
            </>
          )}
          
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', marginTop: '10px' }}>
            JPEG, PNG, WebP, HEIC, AVIF, TIFF up to {IMAGE_CONSTRAINTS.MAX_FILE_SIZE_MB}MB
          </p>
        </div>
      </div>
    </div>
  )
}